<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <button type="button" @click="$router.go(-1)" class="btn-back">
            <font-awesome-icon icon="arrow-left" /> Back
          </button>
          <h5 class="page-title mt-2">Transaction Detail</h5>
          <p class="page-sub">
            <span class="batch-pill" v-if="transaction">{{ transaction.reference }}</span>
          </p>
        </div>
      </div>
  
      <!-- Skeleton -->
      <div v-if="loading" class="form-card mb-4">
        <div class="skeleton-grid">
          <div class="skeleton skeleton--field" v-for="n in 4" :key="n"></div>
        </div>
        <div class="skeleton skeleton--table mt-4"></div>
      </div>
  
      <template v-else-if="transaction">
  
        <!-- Summary Card -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--blue">
              <font-awesome-icon icon="exchange-alt" />
            </div>
            <div>
              <p class="section-title">Transaction Summary</p>
              <p class="section-sub">{{ formatDate(transaction.date) }}</p>
            </div>
            <span class="type-badge ms-auto" :class="typeClass(transaction.type)">
              <font-awesome-icon :icon="typeIcon(transaction.type)" class="me-1" />
              {{ capitalize(transaction.type) }}
            </span>
          </div>
  
          <div class="summary-grid">
            <div class="summary-item">
              <span class="summary-label">Reference</span>
              <span class="summary-value">
                <span class="batch-code">{{ transaction.reference }}</span>
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Total Amount</span>
              <span class="summary-value amount-value fw-bold">{{ formatPrice(transaction.amount) }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Transaction Type</span>
              <span class="summary-value">
                <span class="type-badge" :class="typeClass(transaction.type)">
                  <font-awesome-icon :icon="typeIcon(transaction.type)" class="me-1" />
                  {{ capitalize(transaction.type) }}
                </span>
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Date</span>
              <span class="summary-value">{{ formatDate(transaction.date) }}</span>
            </div>
            <div class="summary-item" v-if="transaction.description">
              <span class="summary-label">Description</span>
              <span class="summary-value text-muted-cell">{{ transaction.description }}</span>
            </div>
            <div class="summary-item" v-if="transaction.source">
              <span class="summary-label">Source</span>
              <span class="summary-value">
                <span class="source-badge">{{ capitalize(transaction.source) }}</span>
              </span>
            </div>
          </div>
        </div>
  
        <!-- Journal Entries -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--teal">
              <font-awesome-icon icon="list-alt" />
            </div>
            <div>
              <p class="section-title">Journal Entries</p>
              <p class="section-sub">{{ entries.length }} debit/credit line{{ entries.length !== 1 ? 's' : '' }}</p>
            </div>
  
            <!-- Balance indicator -->
            <div class="balance-chip ms-auto" :class="isBalanced ? 'balance-chip--ok' : 'balance-chip--err'">
              <font-awesome-icon :icon="isBalanced ? 'check-circle' : 'exclamation-circle'" class="me-1" />
              {{ isBalanced ? 'Balanced' : 'Unbalanced' }}
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Account</th>
                  <th>Code</th>
                  <th class="text-right">Debit</th>
                  <th class="text-right">Credit</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(entry, i) in entries" :key="i" :class="entry.debit > 0 ? 'row--debit' : 'row--credit'">
                  <td class="text-muted-cell">{{ i + 1 }}</td>
                  <td class="fw-bold">{{ entry.account?.name || '—' }}</td>
                  <td>
                    <span class="batch-code" v-if="entry.account?.code">{{ entry.account.code }}</span>
                    <span v-else class="text-muted-cell">—</span>
                  </td>
                  <td class="text-right">
                    <span v-if="entry.debit > 0" class="fw-bold debit-value">{{ formatPrice(entry.debit) }}</span>
                    <span v-else class="text-muted-cell">—</span>
                  </td>
                  <td class="text-right">
                    <span v-if="entry.credit > 0" class="fw-bold credit-value">{{ formatPrice(entry.credit) }}</span>
                    <span v-else class="text-muted-cell">—</span>
                  </td>
                  <td>
                    <span class="entry-badge" :class="entry.debit > 0 ? 'entry--debit' : 'entry--credit'">
                      <font-awesome-icon :icon="entry.debit > 0 ? 'arrow-up' : 'arrow-down'" class="me-1" />
                      {{ entry.debit > 0 ? 'Debit' : 'Credit' }}
                    </span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="totals-row">
                  <td colspan="3" class="totals-label">Totals</td>
                  <td class="text-right fw-bold debit-value">{{ formatPrice(totalDebit) }}</td>
                  <td class="text-right fw-bold credit-value">{{ formatPrice(totalCredit) }}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
  
        <!-- Related Info (if source is sales/purchase) -->
        <div class="form-card mb-4" v-if="transaction.sales || transaction.purchase">
          <div class="section-header">
            <div class="section-icon section-icon--orange">
              <font-awesome-icon icon="link" />
            </div>
            <div>
              <p class="section-title">Related {{ transaction.sales ? 'Sale' : 'Purchase' }}</p>
              <p class="section-sub">Source document linked to this transaction</p>
            </div>
          </div>
          <div class="summary-grid">
            <div class="summary-item" v-if="transaction.sales">
              <span class="summary-label">Sale Ref</span>
              <span class="summary-value">
                <router-link :to="$url('/sales-receipt/' + transaction.sales.id)" class="link-btn">
                  <span class="batch-code">{{ transaction.sales.batch_code }}</span>
                  <font-awesome-icon icon="external-link-alt" class="ms-1" style="font-size:10px" />
                </router-link>
              </span>
            </div>
            <div class="summary-item" v-if="transaction.purchase">
              <span class="summary-label">Purchase Ref</span>
              <span class="summary-value">
                <router-link :to="$url('/purchase-receipt/' + transaction.purchase.id)" class="link-btn">
                  <span class="batch-code">{{ transaction.purchase.batch_code }}</span>
                  <font-awesome-icon icon="external-link-alt" class="ms-1" style="font-size:10px" />
                </router-link>
              </span>
            </div>
            <div class="summary-item" v-if="transaction.sales?.store || transaction.purchase?.store">
              <span class="summary-label">Store</span>
              <span class="summary-value">{{ (transaction.sales || transaction.purchase)?.store?.store_name }}</span>
            </div>
            <div class="summary-item" v-if="transaction.sales?.customer">
              <span class="summary-label">Customer</span>
              <span class="summary-value">{{ transaction.sales.customer?.customer_name }}</span>
            </div>
            <div class="summary-item" v-if="transaction.purchase?.supplier">
              <span class="summary-label">Supplier</span>
              <span class="summary-value">{{ transaction.purchase.supplier?.supplier_name }}</span>
            </div>
          </div>
        </div>
  
      </template>
  
      <!-- Error state -->
      <div v-else class="form-card">
        <div class="state-block">
          <div class="state-icon state-icon--red">
            <font-awesome-icon icon="exclamation-triangle" />
          </div>
          <p class="state-title">Transaction not found</p>
          <p class="state-sub">This transaction may have been deleted or you don't have access to it.</p>
          <button type="button" @click="$router.go(-1)" class="btn-outline mt-3">Go Back</button>
        </div>
      </div>
  
    </div>
  </template>
  
  <script>
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  import moment from 'moment';
  
  export default {
    components: { Header },
  
    data() {
      return {
        transaction:      null,
        entries:          [],
        loading:          true,
        toast:            useToast(),
        store:            useStore(),
      };
    },
  
    computed: {
      permission()  { return this.store.state.getCurrentUserPermission; },
      totalDebit()  { return this.entries.reduce((a, b) => a + parseFloat(b.debit  ?? 0), 0); },
      totalCredit() { return this.entries.reduce((a, b) => a + parseFloat(b.credit ?? 0), 0); },
      isBalanced()  { return Math.abs(this.totalDebit - this.totalCredit) < 0.01; },
    },
  
    created() { this.fetchTransaction(); },
  
    methods: {
      fetchTransaction() {
        this.loading = true;
        axios.get(`/accounting/transactions/${this.$route.params.id}`)
          .then(response => {
            this.loading     = false;
            this.transaction = response.data.data;
            this.entries     = response.data.data.entries ?? [];
          })
          .catch(error => {
            this.loading = false;
            if (error?.response?.status !== 404) {
              this.toast.error(error.response?.data?.message ?? 'Something went wrong');
            }
          });
      },
  
      formatDate(date) { return moment(date).format('DD MMM YYYY'); },
  
      formatPrice(value) {
        if (!value && value !== 0) return '0.00';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
  
      capitalize(text) { return text ? text.charAt(0).toUpperCase() + text.slice(1) : '—'; },
  
      typeClass(type) {
        const map = {
          sales:'type--sales', purchase:'type--purchase', payroll:'type--payroll',
          debit:'type--debit', credit:'type--credit', manual:'type--manual',
        };
        return map[type] ?? 'type--default';
      },
  
      typeIcon(type) {
        const map = {
          sales:'arrow-up', purchase:'arrow-down', payroll:'users',
          debit:'minus-circle', credit:'plus-circle', manual:'pen',
        };
        return map[type] ?? 'exchange-alt';
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header { margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .page-sub    { margin:0; }
  .btn-back    { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; color:#7a90b0; font-size:13px; cursor:pointer; padding:0; margin-bottom:4px; transition:color .15s; }
  .btn-back:hover { color:#1a2b45; }
  .batch-pill  { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }
  
  /* ── Skeleton ── */
  .skeleton-grid  { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .skeleton       { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--field { height:52px; }
  .skeleton--table { height:220px; }
  .mt-4 { margin-top:16px; }
  
  /* ── Form Card ── */
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .mb-4      { margin-bottom:16px; }
  .ms-auto   { margin-left:auto; }
  .ms-1      { margin-left:4px; }
  .mt-3      { margin-top:12px; }
  
  /* ── Section Header ── */
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:20px; flex-wrap:wrap; }
  .section-icon   { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--blue   { background:rgba(0,74,173,.1);   color:#004AAD; }
  .section-icon--teal   { background:rgba(13,148,136,.1); color:#0d9488; }
  .section-icon--orange { background:rgba(224,140,0,.1);  color:#e08c00; }
  .section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub    { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Summary Grid ── */
  .summary-grid  { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; }
  .summary-item  { display:flex; flex-direction:column; gap:6px; }
  .summary-label { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  .summary-value { font-size:13.5px; font-weight:600; color:#1a2b45; }
  .text-muted-cell { color:#4a5e78; font-weight:400; }
  
  /* ── Batch Code ── */
  .batch-code { font-family:monospace; font-size:12.5px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
  
  /* ── Type Badges ── */
  .type-badge     { display:inline-flex; align-items:center; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; }
  .type--sales    { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .type--purchase { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .type--payroll  { background:#f5f3ff; color:#7c3aed; border-color:#c4b5fd; }
  .type--debit    { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .type--credit   { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .type--manual   { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .type--default  { background:#f4f7fb; color:#7a90b0; border-color:#e4eaf2; }
  
  /* ── Source Badge ── */
  .source-badge { display:inline-block; background:#f4f7fb; color:#4a5e78; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; text-transform:capitalize; }
  
  /* ── Balance Chip ── */
  .balance-chip       { display:inline-flex; align-items:center; border-radius:8px; padding:6px 14px; font-size:12.5px; font-weight:700; border:1px solid transparent; }
  .balance-chip--ok   { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .balance-chip--err  { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .me-1 { margin-right:4px; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:11px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 14px; font-size:13.5px; vertical-align:middle; }
  .row--debit  { border-left:3px solid #fac5c5; }
  .row--credit { border-left:3px solid #bbf7d0; }
  .text-right { text-align:right; }
  .fw-bold    { font-weight:700; }
  .amount-value  { color:#004AAD; }
  .debit-value   { color:#e03535; }
  .credit-value  { color:#16a34a; }
  
  /* ── tfoot ── */
  .data-table tfoot .totals-row { background:#f8fafc; border-top:2px solid #e4eaf2; }
  .data-table tfoot td          { padding:12px 14px; font-size:13.5px; }
  .totals-label { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  
  /* ── Entry Badge ── */
  .entry-badge   { display:inline-flex; align-items:center; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; }
  .entry--debit  { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .entry--credit { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  
  /* ── Link Button ── */
  .link-btn { display:inline-flex; align-items:center; text-decoration:none; color:inherit; transition:opacity .15s; }
  .link-btn:hover { opacity:.75; }
  
  /* ── Buttons ── */
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-outline:hover { background:#f4f7fb; }
  
  /* ── State block ── */
  .state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon   { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--red { background:rgba(224,53,53,.1); color:#e03535; }
  .state-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub    { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Responsive ── */
  @media(max-width:991px) { .summary-grid { grid-template-columns:repeat(2,1fr); } .skeleton-grid { grid-template-columns:repeat(2,1fr); } }
  @media(max-width:576px)  { .summary-grid { grid-template-columns:1fr; } .section-header { flex-direction:column; align-items:flex-start; } .balance-chip { width:100%; justify-content:center; } }
  </style>